import knownValidations from "@tinqin/tinqin-utils/src/validations/validation-method-factory/supportedValidations";
import knownFilters from "@tinqin/tinqin-utils/src/filters";
// import configureLogging, { WEBUILogs } from "../utils/appLogging";
import configureLogging, { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

let state = {
    components: {},
    defaultComponentsConfig: {},
    defaultActionsConfig: {},
    reducers: {},
    actions: {},
    sirenActionsMapping: {},
    templates: {},
    webConfig: {
        API_ROOT: "",
        ROOT_PATH: "",
        ROOT_PATH_FULL: "",
        ROOT_PATH_NO_API: "",
        BOOT: "",
        STATIC_CONFIGURATIONS_ROOT: "",
        FLOW_ROOT: "", 
        STEP_ROOT: "",
        MIXIN_ROOT: "",
        WIDGETS_ROOT: ""
    },
    staticTranslations: {}, //object in which we store 'staticApi' translations
    validations: knownValidations,
    filters: knownFilters,
    //will be set to initial Redux state, under "global"
    initialStoreGlobalProperties: {},
    //method for overriding widget/dashboard ui config request urls
    //redirecting to static files within publick directory most often
    staticConfigUrlParser: null,
    //object for complete overriding widget/dashboard ui config requests
    //allowing client application to load static ui configurations using webpack chunks
    //if defined webui will call staticConfigLoader.load() method
    //TODO deprecate
    staticConfigLoader: null,
    //object for complete overriding widget/dashboard ui config requests
    //allowing client application to load modules using webpack chunks
    //if defined webui will call staticConfigLoader.load() method
    //expected result form load method: {config: {}, context?: string}
    lazyModuleLoader: null,
    //global application/component configuration
    //example: locale, dateLocale
    globalProperties: {
        //default locale used for missing translations
        defaultLocale: "en-GB",
        locale: "en-GB",
        //Format:
        //themeID: themeBodyClassName (will be added on change of theme)
        themes: {},
        dashboardPathname: undefined, //expect only 'queryParams' -> to be refactored to true/false
        initialDashboard: undefined, //string
        initialObject: undefined, //string
        staticTranslations: undefined, //bool which enable staticTranslations storage
        staticApiPublicPath: null, //expect string NOT staring with '/'
        staticApiDashboardIdPrefix: undefined, // expect string - dashboards starting with this string are will be requested from 'staticApiPublicPath'
        outputParser: {
            //output parsers storage
            dashboard: {
                widget: () => {}
            }
        }
    },
    dispatchMethod: null,
    context: {}
};

let singleton;

//returns state for a context (based on contextId or widgetId)
//if no contextId defined, return global state
//in case contextId is valid but no context is created with that id, return undefined
const getContextState = ({ contextId = "", widgetId = "" } = {}) => {
    if (!contextId && widgetId) {
        contextId = getWidgetContext(widgetId);
    }
    if (!contextId) {
        return state;
    }
    return state.context[contextId];
};

//extracts context for a given widget
const getWidgetContext = (widgetId) => {
    let dispatch = state.dispatchMethod;
    return dispatch((dispatch, getState) => getState().getIn(["data", widgetId, "context"]));
};

//returns action by name for a given state
const getActionInState = (name, state) => {
    //resolving name1.name2 naming
    if (name.includes(".")) {
        let nameTokens;
        let currentResult;

        nameTokens = name.split(".");

        nameTokens.forEach((token, index) => {
            if (currentResult) {
                currentResult = currentResult[token];
            } else if (index === 0) {
                currentResult = state.actions[token];
            } else {
                return false;
            }
        });
        return currentResult;
    } else {
        return state.actions[name];
    }
};

//default result when searching for missing actions
const createMissingAction = (name) => () => {
    WEBUILogs.error("Unknown action executed: " + name);
};

class InternalStateService {
    constructor() {
        this.log = WEBUILogs.log;
        this.warn = WEBUILogs.warn;
        this.error = WEBUILogs.error;
    }
    static getInstance() {
        if (!singleton) {
            singleton = new InternalStateService();
        }

        return singleton;
    }

    getStaticConfigUrlParser({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.staticConfigUrlParser;
    }

    setStaticConfigUrlParser(parser, { contextId = "", widgetId = "" } = {}) {
        const contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        contextState.staticConfigUrlParser = parser;
    }

    getStaticConfigLoader({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.staticConfigLoader;
    }

    setStaticConfigLoader(loader, { contextId = "", widgetId = "" } = {}) {
        //loader should have a load method
        if (!loader.load) {
            WEBUILogs.error("Incorrent static loader.");
            return;
        }
        const contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        contextState.staticConfigLoader = loader;
    }

    getLazyModuleLoader({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.lazyModuleLoader;
    }

    setLazyModuleLoader(loader, { contextId = "", widgetId = "" } = {}) {
        //loader should have a load method
        if (!loader.load) {
            WEBUILogs.error("Incorrent static loader.");
            return;
        }
        const contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        contextState.lazyModuleLoader = loader;
    }
    //Deprecated?
    setOutputParser(dashboard, widget, parser) {
        if (!state.globalProperties.outputParser[dashboard]) {
            state.globalProperties.outputParser[dashboard] = { [widget]: parser };
        } else {
            state.globalProperties.outputParser[dashboard][widget] = parser;
        }
    }
    //Deprecated?
    getOutputParser(dashboard, widget) {
        return (
            state.globalProperties.outputParser[dashboard] &&
            state.globalProperties.outputParser[dashboard][widget]
        );
    }

    setInitialStoreGlobalProperties(properties) {
        Object.assign(state.initialStoreGlobalProperties, properties);
    }

    getInitialStoreGlobalProperties() {
        return state.initialStoreGlobalProperties;
    }

    setDispatchMethod(dispatchMethod) {
        state.dispatchMethod = dispatchMethod;
    }

    getDispatchMethod() {
        return state.dispatchMethod;
    }
    //TODO: to be deprecated, getComponent is better
    getComponents({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.components;
    }

    getComponent(id, { contextId = "", widgetId = "", strict = false } = {}) {
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return undefined;
        }
        if (strict) {
            return contextState.components[id];
        } else {
            return contextState.components[id] || state.components[id];
        }
    }

    hasComponent(id, { contextId = "", widgetId = "", strict = false } = {}) {
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return false;
        }
        if (strict) {
            return !!contextState.components[id];
        } else {
            return !!contextState.components[id] || !!state.components[id];
        }
    }

    addComponents(extraComponents, { contextId = "", widgetId = "" } = {}) {
        const contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        Object.assign(contextState.components, extraComponents);
    }

    getComponentsDefaultConfig({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.defaultComponentsConfig;
    }

    setComponentsDefaultConfig(config, { contextId = "", widgetId = "" } = {}) {
        const contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        //Note: We don't want to have default config on WebUI level, therefore no Object.assign!
        contextState.defaultComponentsConfig = config;
    }

    getActionsDefaultConfig({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.defaultActionsConfig;
    }

    setActionsDefaultConfig(config, { contextId = "", widgetId = "" } = {}) {
        const contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        // Expect object {actionName: {propertyToSet: value }} eg
        // {update: { requestWrapper: true }}
        contextState.defaultActionsConfig = config;
    }

    getReducers() {
        return state.reducers;
    }

    addReducers(extraReducers) {
        for (var key in extraReducers) {
            if (state.reducers[key]) {
                Object.assign(state.reducers[key], extraReducers[key]);
            } else {
                state.reducers[key] = extraReducers[key];
            }
        }

        return state.reducers;
    }

    getActions({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.actions;
    }

    //return siren action for selected context, or if not defined returns siren action for the global context
    getSirenAction(mappingName, { contextId = "", widgetId = "" } = {}) {
        let contextMapping = this.getSirenActionsMapping({ contextId, widgetId });
        if (contextMapping[mappingName]) {
            return this.getActionByName(contextMapping[mappingName], { contextId, widgetId });
        }
        let globalMapping = this.getSirenActionsMapping();
        if (globalMapping[mappingName]) {
            return this.getActionByName(globalMapping[mappingName]);
        }
        WEBUILogs.error("Missing Siren action: " + mappingName);
    }

    //returns action for the context specified
    //same as get getActionByName, returns true or false
    //searches in current context if not found, searches in the global context
    //if "strict" = true, searches given context only
    hasAction(name, { contextId = "", widgetId = "", strict = false } = {}) {
        let resultAction;
        if (!name) {
            return false;
        }
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            if (strict) {
                return false;
            }
            contextState = state;
        }
        resultAction = getActionInState(name, contextState);
        if (!resultAction) {
            if (strict) {
                return false;
            }
            resultAction = getActionInState(name, state);
        }
        return !!resultAction;
    }

    //accepts complex action names
    //example: entity.update
    //searches in current context if not found, searches in the global context
    //if "strict" = true, will search only in the context provided via contextId or widgetId
    getActionByName(name, { contextId = "", widgetId = "", strict = false } = {}) {
        let resultAction;
        if (!name) {
            WEBUILogs.log("Missing action: " + name);
            return createMissingAction(name);
        }
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            if (strict) {
                return false;
            }
            contextState = state;
        }
        resultAction = getActionInState(name, contextState);
        if (!resultAction) {
            if (strict) {
                return createMissingAction(name);
            }
            resultAction = getActionInState(name, state);
        }
        if (!resultAction) {
            return createMissingAction(name);
        }
        return resultAction;
    }

    //adds actions for specified context or global context
    addActions(extraActions = {}, { contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        Object.keys(extraActions).forEach((newKey) => {
            if (contextState.actions[newKey]) {
                contextState.actions[newKey] = Object.assign(
                    {},
                    contextState.actions[newKey],
                    extraActions[newKey]
                );
            } else {
                contextState.actions[newKey] = extraActions[newKey];
            }
        });
    }
    // by default returns validations that are defined on project level
    // in case of nested webui contexts, returns merged global validations with context's specific validations
    getValidations({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return state.validations;
        }
        if (contextState !== state) {
            return Object.assign({}, state.validations, contextState.validations);
        }
        return contextState.validations;
    }

    addValidations(validations, { contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        Object.assign(contextState.validations, validations);
    }

    getFilters({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.filters;
    }

    addFilters(filters, { contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        Object.assign(contextState.filters, filters);
    }

    addSirenActionsMapping(additionalSirenActionsMapping, { contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        Object.assign(contextState.sirenActionsMapping, additionalSirenActionsMapping);
    }

    //returns siren actions mapping for given context or global context
    getSirenActionsMapping({ contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.sirenActionsMapping;
    }

    getWebConfig() {
        return state.webConfig;
    }

    setWebConfig(webConfig) {
        state.webConfig = webConfig;
    }

    getDashboardTemplates() {
        return state.templates;
    }

    addDashboardTemplates(extraTemplates, { contextId = "", widgetId = "" } = {}) {
        let contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        Object.assign(contextState.templates, extraTemplates);
    }

    getGlobalProperty(name) {
        return state.globalProperties[name];
    }

    getGlobalProperties(name) {
        // If we pass in the name of a global property the method will return the value of that property.
        if (name) {
            return state.globalProperties[name];
        }
        //If we don't pass the name of a particular property, we return all the global properties.
        return Object.assign({}, state.globalProperties);
    }

    setGlobalProperties(globalProperties) {
        state.globalProperties = Object.assign(state.globalProperties, globalProperties);
    }

    changeLocale(locale) {
        WEBUILogs.log("Locale changed to: " + locale);

        this.setGlobalProperties({
            locale: locale
        });
    }

    getStaticTranslation(language) {
        if (language) {
            return state.staticTranslations[language];
        }
        return Object.assign({}, state.staticTranslations);
    }

    setStaticTranslation(language) {
        state.staticTranslations = Object.assign(state.staticTranslations, language);
        WEBUILogs.log(state.staticTranslations);
    }

    setConstructRequestEntityMethod(method, { contextId = "", widgetId = "" } = {}) {
        const contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        contextState.customConstructRequestEntityMethod = method;
    }

    getConstructRequestEntityMethod({ contextId = "", widgetId = "" } = {}) {
        const contextState = getContextState({ contextId, widgetId }) || state;
        return (
            contextState.customConstructRequestEntityMethod ||
            state.customConstructRequestEntityMethod
        );
    }

    setSerializeMethod(method, { contextId = "", widgetId = "" } = {}) {
        const contextState = getContextState({ contextId, widgetId });
        if (!contextState) {
            return;
        }
        contextState.customSerializeMethod = method;
    }

    getSerializeMethod({ contextId = "", widgetId = "" } = {}) {
        const contextState = getContextState({ contextId, widgetId }) || state;
        return contextState.customSerializeMethod || state.customSerializeMethod;
    }

    getContextConfig(id) {
        return state.context[id] ? state.context[id].config : {};
    }

    getWidgetContext(widgetId) {
        return getWidgetContext(widgetId);
    }

    getWidgetContextPath(widgetId) {
        let widgetContext = getWidgetContext(widgetId);
        if (state.context[widgetContext]) {
            return state.context[widgetContext].config["path"];
        }
    }
    //creating multiple contexts with the same name overrides their "config" property
    //"config" example: { "path": "/oav" }
    createContext(id, config = {}) {
        //overriding current context state if any
        let initialContextState = state.context[id] || {
            components: {},
            actions: {},
            defaultComponentsConfig: {},
            defaultActionsConfig: {},
            sirenActionsMapping: {},
            templates: {},
            validations: {}
        };
        initialContextState.config = config;
        state.context[id] = initialContextState;
    }

    deleteContext(id) {
        if (state.context[id]) {
            delete state.context[id];
        }
    }
    stopLogging() {
        configureLogging(false);
    }
    getAllContextIds() {
        return Object.keys(state.context);
    }
}

export default InternalStateService.getInstance();
